/**
 * The pipeline's stages, and which of them are read-only.
 *
 * The stage names were magic strings spread across WorkingPanel; they are shared state now (ChatPanel
 * locks itself on them, SessionLayout triggers the mapping on them), so they live in one place.
 *
 * ## Why a stage can be "read-only" at all
 *
 * Nothing in this UI edits a cell — `DataPreviewTable` has no editing surface, and on Upload & view and
 * Map & review it is not even given the selection handlers. The ONE thing that can change data is a chat
 * message: the agent answers it with `run_python` / `create_dataframe` / `set_header_row`. So making a
 * stage read-only means locking the composer on it, and nothing else.
 *
 * Upload & view is "get the file in and look at it"; Map & review is "approve what will be written into
 * the task" (see data-sorting/CLAUDE.md — that stage exists so a human reads the rows first). Preview &
 * transform is the workbench, and Commit needs the composer for the same reason it always did.
 *
 * Map & review (STAGE_LIVE, the "Preview" tab) now renders the same body as Commit and, like Commit, does
 * not show the chat at all (SessionLayout's `showChat`). It stays in LOCKED_STAGES anyway: the lock is
 * still the right answer for that stage, and it keeps a send from Preview reading as "an answer to the
 * mapping turn" in useAutoMap's `noteUserSend`.
 */

export const STAGE_SOURCE = "source";
export const STAGE_WORKING = "sample";
export const STAGE_LIVE = "live";
export const STAGE_COMMIT = "commit";
// Not a pipeline stage — an accumulating board that sits alongside them, and currently hidden behind
// WorkingPanel's SHOW_CHARTS. Listed so the lock does not have to guess about it.
export const STAGE_CHARTS = "charts";

// The two stages where a change may not be STARTED. Order is presentation order, for the tests to read.
export const LOCKED_STAGES = [STAGE_SOURCE, STAGE_LIVE];

/**
 * Is the composer locked on this stage?
 *
 * `undefined` is deliberately NOT locked: `canSendMessage` is called from places that do not care about
 * the stage, and a missing argument must not silently disable the chat.
 */
export function isStageLocked(stage) {
  return LOCKED_STAGES.includes(stage);
}

/** Why the composer is closed, naming the way out. Null when it is not closed by the stage. */
export function stageLockReason(stage, { hasSourceData = true } = {}) {
  if (!isStageLocked(stage)) return null;
  // A session with nothing in it yet: the way out is a file, not another tab. Saying "go to Preview &
  // transform" here would point at a tab that is disabled until something is uploaded.
  if (!hasSourceData) return "Add a source file to get started — the drop zone is on the right";
  if (stage === STAGE_LIVE) {
    return "Map & review is read-only — go back to Preview & transform to make changes";
  }
  return "Upload & view is read-only — open Preview & transform to work on your data";
}
